
import React, { useState, useCallback } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQAccordionProps {
  faqs: FAQItem[];
  className?: string;
}

const FAQAccordion = ({ faqs, className = '' }: FAQAccordionProps) => {
  // Track which FAQ item is open by index
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  
  // Memoize the toggle handler to avoid recreating it on each render
  const handleToggle = useCallback((index: number) => {
    setOpenIndex(prevIndex => prevIndex === index ? null : index);
  }, []);

  return (
    <div className={className}>
      <h2 className="text-3xl md:text-4xl font-montserrat font-bold mb-8 text-center text-[#3E2723]">Frequently Asked Questions</h2>
      <div className="space-y-4">
        {faqs.map((faq, index) => {
          const isOpen = openIndex === index;
          
          return (
            <div
              key={index}
              className="bg-white rounded-lg overflow-hidden border border-gold/20 group"
            >
              <button
                onClick={() => handleToggle(index)}
                className="w-full flex justify-between items-center p-4 font-montserrat font-semibold text-[#3E2723] cursor-pointer hover:bg-neutral-tan/30 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:ring-inset transition-colors text-left"
                aria-expanded={isOpen}
                aria-controls={`faq-content-${index}`}
              >
                <span className="text-lg">{faq.question}</span>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-5 w-5 transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                  viewBox="0 0 20 20" 
                  fill="#C58B65"
                  aria-hidden="true"
                >
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              
              <div 
                id={`faq-content-${index}`}
                className={`overflow-hidden transition-all duration-300 ${
                  isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
                aria-hidden={!isOpen}
              >
                <div className="p-4 pt-2 text-[#3E2723]/90 font-montserrat border-t border-gold/10">
                  {faq.answer}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Use React.memo to prevent unnecessary re-renders
export default React.memo(FAQAccordion);
